import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import useIamStore from '../store/useIamStore';
import useV2Auth from '@/modules/system/store/useV2Auth';
import { Button } from '@/components/ui-v2';
import { showSuccessToast, showErrorToast } from '@/services/toastService';
import InviteMembersModal from './InviteMembersModal';
import ChangeRoleModal from './ChangeRoleModal';
import PromoteOrgAdminModal from './PromoteOrgAdminModal';
import DemoteOrgAdminModal from './DemoteOrgAdminModal';
import RemoveMemberModal from './RemoveMemberModal';

type Member = ReturnType<typeof useIamStore.getState>['members'][number];

interface RolePickerArgs {
    memberId: string;
    memberName: string;
    workspaceId: string;
    workspaceName: string;
    fromRoleName: string | null;
    toRoleId: string;
    toRoleName: string;
}

export default function MembersPage() {
    const {
        members,
        roles,
        invites,
        fetchMembers,
        fetchRoles,
        fetchInvites,
        setMemberWorkspaceRole,
        removeMemberWorkspaceRole,
        setMemberOrgAdmin,
        removeMember,
        resendInvite,
        revokeInvite,
        error,
    } = useIamStore();
    const { hasPermission, workspaces, currentWorkspaceId, organization } = useV2Auth();

    const canManageMembers = hasPermission('workspace.manage_members') || hasPermission('organization.edit');
    const isOrgAdmin = hasPermission('organization.edit');

    const [searchParams, setSearchParams] = useSearchParams();
    const [inviteOpen, setInviteOpen] = useState(searchParams.get('invite') === '1');

    useEffect(() => {
        if (searchParams.get('invite') === '1') {
            setInviteOpen(true);
            const next = new URLSearchParams(searchParams);
            next.delete('invite');
            setSearchParams(next, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const [pendingChangeRole, setPendingChangeRole] = useState<RolePickerArgs | null>(null);
    const [promoteTarget, setPromoteTarget] = useState<Member | null>(null);
    const [demoteTarget, setDemoteTarget] = useState<Member | null>(null);
    const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (canManageMembers) {
            fetchMembers();
            fetchRoles();
            fetchInvites();
        }
    }, [canManageMembers, fetchMembers, fetchRoles, fetchInvites]);

    const wsRoles = useMemo(() => roles.filter((r) => r.scope === 'workspace'), [roles]);
    const visibleWorkspaces = useMemo(
        () => (isOrgAdmin ? workspaces : workspaces.filter((w) => w._id === currentWorkspaceId)),
        [isOrgAdmin, workspaces, currentWorkspaceId],
    );
    const orgName = organization?.name || 'this organization';

    if (!canManageMembers) {
        return (
            <div className="max-w-4xl mx-auto p-6">
                <div
                    className="rounded-lg p-6 text-center"
                    style={{
                        background: 'var(--bg-warning)',
                        border: '1px solid var(--border-muted)',
                    }}
                >
                    <h2 className="text-lg font-medium" style={{ color: 'var(--fg-warning)' }}>
                        Access denied
                    </h2>
                    <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>
                        You don't have permission to manage members.
                    </p>
                </div>
            </div>
        );
    }

    const memberDisplayName = (m: Member) =>
        m.firstName || m.lastName ? `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() : m.email;

    const handlePickRole = (member: Member, workspaceId: string, newRoleId: string) => {
        if (!newRoleId) {
            // Treat empty as "remove from workspace"
            const ws = workspaces.find((w) => w._id === workspaceId);
            const existing = member.memberships.find((mb) => mb.workspaceId === workspaceId);
            if (!existing) return;
            const proceed = window.confirm(
                `Remove ${memberDisplayName(member)} from ${ws?.name || 'this workspace'}?`,
            );
            if (!proceed) return;
            void (async () => {
                setSubmitting(true);
                try {
                    await removeMemberWorkspaceRole(member._id, { workspaceId });
                    showSuccessToast('Removed from workspace.');
                } catch (e) {
                    const err = e as { response?: { data?: { error?: string } }; message?: string };
                    showErrorToast(err.response?.data?.error || err.message || 'Failed to update');
                } finally {
                    setSubmitting(false);
                }
            })();
            return;
        }

        const newRole = wsRoles.find((r) => r._id === newRoleId);
        const ws = workspaces.find((w) => w._id === workspaceId);
        if (!newRole || !ws) return;

        const existing = member.memberships.find((mb) => mb.workspaceId === workspaceId);
        if (existing && existing.roleId === newRoleId) return;

        setPendingChangeRole({
            memberId: member._id,
            memberName: memberDisplayName(member),
            workspaceId,
            workspaceName: ws.name,
            fromRoleName: existing?.roleName ?? null,
            toRoleId: newRoleId,
            toRoleName: newRole.name,
        });
    };

    const confirmChangeRole = async () => {
        if (!pendingChangeRole) return;
        setSubmitting(true);
        try {
            await setMemberWorkspaceRole(pendingChangeRole.memberId, {
                workspaceId: pendingChangeRole.workspaceId,
                roleId: pendingChangeRole.toRoleId,
            });
            showSuccessToast("Role updated. They've been emailed.");
            setPendingChangeRole(null);
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Failed to update');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmPromote = async () => {
        if (!promoteTarget) return;
        setSubmitting(true);
        try {
            await setMemberOrgAdmin(promoteTarget._id, { isOrgAdmin: true });
            showSuccessToast(`${memberDisplayName(promoteTarget)} is now an org admin.`);
            setPromoteTarget(null);
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Failed to promote');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmDemote = async (fallbackWorkspaceRoles: { workspaceId: string; roleId: string }[]) => {
        if (!demoteTarget) return;
        setSubmitting(true);
        try {
            await setMemberOrgAdmin(demoteTarget._id, { isOrgAdmin: false, fallbackWorkspaceRoles });
            showSuccessToast('Org admin removed.');
            setDemoteTarget(null);
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Failed to demote');
        } finally {
            setSubmitting(false);
        }
    };

    const confirmRemove = async () => {
        if (!removeTarget) return;
        setSubmitting(true);
        try {
            await removeMember(removeTarget._id);
            showSuccessToast(`${memberDisplayName(removeTarget)} removed.`);
            setRemoveTarget(null);
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Failed to remove');
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendInvite = async (id: string) => {
        try {
            await resendInvite(id);
            showSuccessToast('Invite re-sent.');
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Failed to resend');
        }
    };

    const handleRevokeInvite = async (id: string) => {
        if (!window.confirm('Cancel this pending invite?')) return;
        try {
            await revokeInvite(id);
            showSuccessToast('Invite cancelled.');
        } catch (e) {
            const err = e as { response?: { data?: { error?: string } }; message?: string };
            showErrorToast(err.response?.data?.error || err.message || 'Failed to revoke');
        }
    };

    const renderMemberRow = (m: Member) => {
        const name = memberDisplayName(m);
        const wsRolesByWs = new Map<string, string>();
        m.memberships.forEach((mb) => {
            if (mb.workspaceId) wsRolesByWs.set(mb.workspaceId, mb.roleId);
        });

        const canChangeOrgAdmin = isOrgAdmin;

        return (
            <div key={m._id} className="px-5 py-4">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                            {name}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                            {m.email}
                        </p>
                    </div>

                    <div className="flex items-center gap-2.5 shrink-0">
                        {m.isOrgAdmin ? (
                            <span
                                className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                                style={{
                                    background: 'var(--accent-muted)',
                                    color: 'var(--accent-emphasis)',
                                }}
                            >
                                Organization admin
                            </span>
                        ) : null}

                        {canChangeOrgAdmin &&
                            (m.isOrgAdmin ? (
                                <button
                                    onClick={() => setDemoteTarget(m)}
                                    className="text-xs transition-colors"
                                    style={{ color: 'var(--text-tertiary)' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--text-primary)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-tertiary)')}
                                >
                                    Demote
                                </button>
                            ) : (
                                <button
                                    onClick={() => setPromoteTarget(m)}
                                    className="text-xs transition-colors"
                                    style={{ color: 'var(--accent)' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--accent-emphasis)')}
                                    onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--accent)')}
                                >
                                    Make org admin
                                </button>
                            ))}

                        {isOrgAdmin && (
                            <button
                                onClick={() => setRemoveTarget(m)}
                                className="text-xs transition-colors"
                                style={{ color: 'var(--text-tertiary)' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = 'var(--fg-danger)')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = 'var(--text-tertiary)')}
                            >
                                Remove
                            </button>
                        )}
                    </div>
                </div>

                {!m.isOrgAdmin && (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                        {visibleWorkspaces.map((ws) => {
                            const currentRoleId = wsRolesByWs.get(ws._id) || '';
                            const canEditThisWs = isOrgAdmin || ws._id === currentWorkspaceId;
                            return (
                                <div
                                    key={ws._id}
                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md"
                                    style={{
                                        background: 'var(--surface-inset)',
                                        border: '1px solid var(--border-muted)',
                                    }}
                                >
                                    <span className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>
                                        {ws.name}
                                    </span>
                                    {canEditThisWs ? (
                                        <select
                                            value={currentRoleId}
                                            onChange={(e) => handlePickRole(m, ws._id, e.target.value)}
                                            disabled={submitting}
                                            className="bg-transparent text-xs font-medium outline-none cursor-pointer"
                                            style={{ color: 'var(--text-primary)' }}
                                        >
                                            <option value="">— No access —</option>
                                            {wsRoles.map((r) => (
                                                <option key={r._id} value={r._id}>
                                                    {r.name}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                                            {currentRoleId
                                                ? wsRoles.find((r) => r._id === currentRoleId)?.name || '—'
                                                : 'No access'}
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {m.isOrgAdmin && (
                    <p
                        className="mt-2 text-xs italic"
                        style={{ color: 'var(--text-tertiary)' }}
                    >
                        Org admins have access to every workspace by default.
                    </p>
                )}
            </div>
        );
    };

    const wsName = (id?: string | null) => workspaces.find((w) => w._id === id)?.name || '—';

    const cardStyle: React.CSSProperties = {
        background: 'var(--surface-primary)',
        border: '1px solid var(--border-muted)',
    };

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-between mb-6">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    {members.length} {members.length === 1 ? 'person' : 'people'} in {orgName}
                </p>
                <Button variant="primary" onClick={() => setInviteOpen(true)}>
                    + Invite people
                </Button>
            </div>

            {error && (
                <div
                    className="mb-4 rounded-lg px-4 py-3"
                    style={{ background: 'var(--bg-danger)', border: '1px solid var(--border-muted)' }}
                >
                    <p className="text-sm" style={{ color: 'var(--fg-danger)' }}>
                        {error}
                    </p>
                </div>
            )}

            <section className="mb-7">
                <h3
                    className="text-[10px] font-medium uppercase tracking-[0.08em] mb-2.5"
                    style={{ color: 'var(--text-tertiary)' }}
                >
                    Active
                </h3>
                {members.length === 0 ? (
                    <p className="text-sm rounded-xl p-5" style={{ ...cardStyle, color: 'var(--text-tertiary)' }}>
                        No members yet. Invite someone to get started.
                    </p>
                ) : (
                    <div
                        className="rounded-xl overflow-hidden"
                        style={{ ...cardStyle, boxShadow: 'var(--shadow-sm)' }}
                    >
                        {members.map((m, i) => (
                            <div
                                key={m._id}
                                style={i > 0 ? { borderTop: '1px solid var(--border-muted)' } : undefined}
                            >
                                {renderMemberRow(m)}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section>
                <h3
                    className="text-[10px] font-medium uppercase tracking-[0.08em] mb-2.5"
                    style={{ color: 'var(--text-tertiary)' }}
                >
                    Pending invites
                    <span className="ml-1 normal-case font-normal" style={{ color: 'var(--text-quaternary)' }}>
                        ({invites.length})
                    </span>
                </h3>
                {invites.length === 0 ? (
                    <p className="text-sm rounded-xl p-5" style={{ ...cardStyle, color: 'var(--text-tertiary)' }}>
                        No pending invites.
                    </p>
                ) : (
                    <div
                        className="rounded-xl overflow-hidden"
                        style={{ ...cardStyle, boxShadow: 'var(--shadow-sm)' }}
                    >
                        {invites.map((inv, i) => {
                            const roleObj = typeof inv.roleId === 'object' ? inv.roleId : null;
                            const roleName = roleObj?.name || 'Unknown';
                            const roleScope = roleObj?.scope || '';
                            const inviterEmail = typeof inv.invitedBy === 'object' ? inv.invitedBy.email : '';
                            const expiresIn = Math.max(
                                0,
                                Math.ceil((new Date(inv.expiresAt).getTime() - Date.now()) / (24 * 60 * 60 * 1000)),
                            );
                            return (
                                <div
                                    key={inv._id}
                                    className="flex items-center justify-between px-5 py-3.5"
                                    style={i > 0 ? { borderTop: '1px solid var(--border-muted)' } : undefined}
                                >
                                    <div className="min-w-0">
                                        <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                                            {inv.email}
                                        </p>
                                        <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
                                            {roleScope === 'organization'
                                                ? `${roleName} · org admin`
                                                : `${roleName} in ${wsName(inv.workspaceId)}`}
                                            {' · '}
                                            Invited by {inviterEmail}
                                            {' · '}
                                            {expiresIn > 0 ? `Expires in ${expiresIn}d` : 'Expired'}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleResendInvite(inv._id)}
                                            className="text-xs px-2.5 py-1 rounded-md transition-colors"
                                            style={{
                                                color: 'var(--text-secondary)',
                                                border: '1px solid var(--border-default)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'var(--surface-hover)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            Resend
                                        </button>
                                        <button
                                            onClick={() => handleRevokeInvite(inv._id)}
                                            className="text-xs px-2.5 py-1 rounded-md transition-colors"
                                            style={{
                                                color: 'var(--fg-danger)',
                                                border: '1px solid var(--border-muted)',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'var(--bg-danger)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'transparent';
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </section>

            <InviteMembersModal
                open={inviteOpen}
                onClose={() => setInviteOpen(false)}
                onSent={() => {
                    fetchInvites();
                    fetchMembers();
                }}
                roles={roles}
                workspaces={workspaces}
                currentWorkspaceId={currentWorkspaceId}
                isOrgAdmin={isOrgAdmin}
            />

            {pendingChangeRole && (
                <ChangeRoleModal
                    open
                    onClose={() => setPendingChangeRole(null)}
                    onConfirm={confirmChangeRole}
                    memberName={pendingChangeRole.memberName}
                    workspaceName={pendingChangeRole.workspaceName}
                    fromRoleName={pendingChangeRole.fromRoleName}
                    toRoleName={pendingChangeRole.toRoleName}
                    submitting={submitting}
                />
            )}

            {promoteTarget && (
                <PromoteOrgAdminModal
                    open
                    onClose={() => setPromoteTarget(null)}
                    onConfirm={confirmPromote}
                    memberName={memberDisplayName(promoteTarget)}
                    orgName={orgName}
                    submitting={submitting}
                />
            )}

            {demoteTarget && (
                <DemoteOrgAdminModal
                    open
                    onClose={() => setDemoteTarget(null)}
                    onConfirm={confirmDemote}
                    memberName={memberDisplayName(demoteTarget)}
                    orgName={orgName}
                    workspaces={workspaces}
                    roles={roles}
                    submitting={submitting}
                />
            )}

            {removeTarget && (
                <RemoveMemberModal
                    open
                    onClose={() => setRemoveTarget(null)}
                    onConfirm={confirmRemove}
                    memberName={memberDisplayName(removeTarget)}
                    memberEmail={removeTarget.email}
                    orgName={orgName}
                    submitting={submitting}
                />
            )}
        </div>
    );
}
